
import FalconCardHeader from '../../../../../../components/common/FalconCardHeader';
import React, { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Card, Col, Form, Row } from "react-bootstrap";
import CardDropdown from "../../../../../../components/common/CardDropdown";
import VelocidadChart from "./VelocidadChart";
import Flex from "../../../../../../components/common/Flex";

const groupByDate = (data) => {
  const grouped = {};
  data.forEach((alert) => {
    const formattedDate = dayjs(alert.fecha, "YYYY/MM/DD HH:mm:ss").format(
      "YYYY-MM-DD"
    );
    if (!grouped[formattedDate]) {
      grouped[formattedDate] = [];
    }
    grouped[formattedDate].push(alert);
  });
  return grouped;
};

const groupByHour = (data) => {
  const grouped = {};
  data.forEach((alert) => {
    const formattedHour = dayjs(alert.fecha, "YYYY/MM/DD HH:mm:ss").format(
      "HH:00"
    );
    if (!grouped[formattedHour]) {
      grouped[formattedHour] = [];
    }
    grouped[formattedHour].push(alert);
  });
  return grouped;
};

// Calcula promedios y máximos por fecha/hora
const calcularPromedios = (data) => {
  const promedios = [];
  const velocidades = [];
  for (const fecha in data) {
    const vehiculos = Array.isArray(data[fecha]) ? data[fecha] : [];
    if (vehiculos.length === 0) continue;
    let promedio_diario = [];
    vehiculos.forEach((p) => {
      if ((p.tipo === 1 || p.tipo === 2 || p.tipo === 4 || p.tipo === 6) && p.tipoMotor === 1) {
        promedio_diario.push(p.velocidad);
        velocidades.push(p.velocidad);
      } else if ((p.tipo === 3 || p.tipo === 5) && p.tipoMotor === 0) {
        promedio_diario.push(p.velocidad);
        velocidades.push(p.velocidad);
      }
    });
    const totalVelocidad = promedio_diario.reduce((sum, item) => sum + item, 0);
    const promedio = promedio_diario.length > 0 ? totalVelocidad / promedio_diario.length : 0;
    promedios.push(promedio);
  }
  const promVelDiaria =
    velocidades.length > 0
      ? (velocidades.reduce((sum, item) => sum + item, 0) / velocidades.length).toFixed(2)
      : 0;
  return { promedios, promVelDiaria };
};

const calcularVelMaxima = (data) => {
  const maximasPorDia = [];
  let maximaAbsoluta = 0;

  for (const fecha in data) {
    const registros = Array.isArray(data[fecha]) ? data[fecha] : [];
    if (registros.length === 0) {
      maximasPorDia.push(0); // No grafica nada para ese día
      continue;
    }

    const velocidades = registros
      /*   .filter(registro =>
          (registro.tipoMotor === 1 && [1, 2, 4, 6].includes(registro.tipo)) ||
          (registro.tipoMotor === 0 && [3, 5].includes(registro.tipo))
        ) */
      .map(registro => registro.velocidad)
      .filter(v => !isNaN(v));

    if (velocidades.length === 0) {
      maximasPorDia.push(0); // No grafica si no hay velocidades válidas
      continue;
    }

    const maximaDia = Math.max(...velocidades);
    maximasPorDia.push(maximaDia);
  }
  const velMax = Math.max(...maximasPorDia);

  return { maximasPorDia: maximasPorDia, promVelMax: velMax };
};
const calcularMaximos = (data) => {
  const velocidadesMaximas = [];
  for (const fecha in data) {
    const vehiculos = Array.isArray(data[fecha]) ? data[fecha] : [];
    if (vehiculos.length === 0) continue;
    const totalMaxima = vehiculos.reduce(
      (sum, vehiculo) => sum + (vehiculo.vehiculos?.velocidadMaxima || 0),
      0
    );
    const maxima = vehiculos.length > 0 ? Math.round(totalMaxima / vehiculos.length) : 0;
    velocidadesMaximas.push(maxima);
  }
  const velMax =
    velocidadesMaximas.length > 0
      ? Math.round(
        velocidadesMaximas.reduce((sum, item) => sum + item, 0) /
        velocidadesMaximas.length
      )
      : 0;
  return { maximos: velocidadesMaximas, velMax };
};

const Velocidad = ({ data }) => {

  const chartRef = useRef(null);

  // Agrupar alertas
  const groupedByDate = useMemo(() => groupByDate(data), [data]);
  const groupedByHour = useMemo(() => groupByHour(data), [data]);
  const uniqueDates = Object.keys(groupedByDate);
  const isSingleDay = uniqueDates.length === 1;
  const chartData = isSingleDay ? groupedByHour : groupedByDate;

  // Calcular promedios y máximos
  const { promedios, promVelDiaria } = useMemo(() => calcularPromedios(chartData), [chartData]);
  const { maximasPorDia, promVelMax } = useMemo(() => calcularVelMaxima(chartData), [chartData]);
  const { maximos, velMax } = useMemo(() => calcularMaximos(chartData), [chartData]);

  const handleLegendToggle = (name) => {
    chartRef.current.getEchartsInstance().dispatchAction({
      type: "legendToggleSelect",
      name,
    });
  };

  let velPromedioStr = `${` ${promVelDiaria}`} km/h`;
  let velMaxStr = `${` ${promVelMax}`} km/h`;
  let velMaxConfigStr = `${` ${velMax}`} km/h`;
  return (
    <Card className="h-100">


      <FalconCardHeader
        title={"Velocidad"}
        light
        titleTag="h6"
      ></FalconCardHeader>



      <Card.Body className="flex-grow-1 pe-xxl-0" style={{ minHeight: '400px' }}>

        <Row className='flex-nowrap px-3 justify-content-between' >
          <Col xs={4} className="flex-shrink-0 p-0 me-2" title={'Vel. diaria promedio: ' + velPromedioStr}>
            <Form.Check
              type="checkbox"
              id="velocidadDiariaPromedio"
              className="mb-0 d-inline-flex flex-nowrap align-items-center w-100"
            >
              <Form.Check.Input
                type="checkbox"
                className="form-check-input-primary"
                onClick={() => handleLegendToggle("Vel. diaria promedio")}
                defaultChecked
              />
              <span
                className="ps-2 fs-11 label-normal mb-0 mt-1"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  maxWidth: "100%",
                  verticalAlign: "middle"
                }}
              >
                Vel. diaria promedio:
              </span>
              <span className="fs-11 label-bold mt-1 ps-2 text-truncate" style={{ minWidth: '100px' }}>{velPromedioStr}</span>
            </Form.Check>
          </Col>
          <Col xs={4} className="flex-shrink-0 p-0 me-2" title={'Vel. máxima: ' + velMaxStr}>
            <Form.Check
              type="checkbox"
              id="velocidadMax"
              className="mb-0 d-inline-flex flex-nowrap align-items-center w-100 justify-content-center"
            >
              <Form.Check.Input
                type="checkbox"
                className="form-check-input-danger"
                onClick={() => handleLegendToggle("Vel. máxima")}
                defaultChecked
              />
              <span
                className="ps-2 fs-11 label-normal mb-0 mt-1"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  maxWidth: "100%",
                  verticalAlign: "middle"
                }}
              >
                Vel. máxima:
              </span>
              <span className="fs-11 label-bold mt-1 ps-2 text-truncate" style={{ minWidth: '100px' }}>{velMaxStr}</span>
            </Form.Check>
          </Col>
          <Col xs={4} className="flex-shrink-0 p-0 me-2 " title={'Lím. velocidad:' + velMaxConfigStr}>
            <Form.Check
              type="checkbox"
              id="ecommercePrevYear"
              className="mb-0  d-inline-flex flex-nowrap align-items-center w-100 justify-content-end"
            >
              <Form.Check.Input
                type="checkbox"
                className="form-check-input-warning"
                onClick={() => handleLegendToggle("Lím. velocidad")}
                defaultChecked
              />
              <span
                className="ps-2 fs-11 label-normal mb-0 mt-1"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  maxWidth: "100%",
                  verticalAlign: "middle"
                }}
              >
                Lím. velocidad:
              </span>
              <span className="fs-11 label-bold mt-1 ps-2 text-truncate" style={{ minWidth: '100px' }}>{velMaxConfigStr}</span>
            </Form.Check>
          </Col>
        </Row>
        <VelocidadChart
          data={chartData}
          promedioPorFecha={promedios}
          maximasPorDia={maximasPorDia}
          maximaPorFecha={maximos}
          ref={chartRef}
          isSingleDay={isSingleDay}
          style={{ height: '90%', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

export default Velocidad;





// import React, { useEffect, useMemo, useRef, useState } from "react";
// import dayjs from "dayjs";
// import { Card, Col, Form, Row } from "react-bootstrap";
// import CardDropdown from "../../../../../../components/common/CardDropdown";
// import VelocidadChart from "./VelocidadChart";
// import Flex from "../../../../../../components/common/Flex";
// const groupByDate = (data) => {
//   const grouped = {};

//   data.forEach((alert) => {
//     const formattedDate = dayjs(alert.fecha, "YYYY/MM/DD HH:mm:ss").format(
//       "YYYY-MM-DD"
//     ); // 🔹 Agrupar solo por día
//     if (!grouped[formattedDate]) {
//       grouped[formattedDate] = [];
//     }
//     // Agregar el objeto actual al arreglo correspondiente a la fecha
//     grouped[formattedDate].push(alert);
//   });

//   return grouped;
// };

// const groupByHour = (data) => {
//   const grouped = {};

//   data.forEach((alert) => {
//     const formattedHour = dayjs(alert.fecha, "YYYY/MM/DD HH:mm:ss").format(
//       "HH:00"
//     ); // 🔹 Agrupar solo por hora
//     if (!grouped[formattedHour]) {
//       grouped[formattedHour] = [];
//     }
//     // Agregar el objeto actual al arreglo correspondiente a la fecha
//     grouped[formattedHour].push(alert);
//   });

//   return grouped;
// };

// const Velocidad = ({ data }) => {
//   const chartRef = useRef(null);
//   const [velDiariaProm, setVelDiariaProm] = useState(0);
//   const [velMaxima, setVelMaxima] = useState(0);

//   // 🔹 Agrupar alertas
//   const groupedByDate = useMemo(() => groupByDate(data), [data]);
//   const groupedByHour = useMemo(() => groupByHour(data), [data]);

//   // 🔹 Determinar si solo hay un día en los datos
//   const uniqueDates = Object.keys(groupedByDate);
//   const isSingleDay = uniqueDates.length === 1;

//   const handleLegendToggle = (name) => {
//     chartRef.current.getEchartsInstance().dispatchAction({
//       type: "legendToggleSelect",
//       name,
//     });
//   };

//   return (
//     <Card className="h-100 mt-3">
//       <Card.Header>
//         <Row className="flex-between-center g-0">
//           <Col xs="auto">
//             <h6 className="mb-0">Velocidad</h6>
//           </Col>
//           <Col md="auto" className="order-1 order-md-0 mt-3 mt-md-0">
//             <Flex className="flex-wrap gap-2 gap-md-3">
//               <Form.Check
//                 type="checkbox"
//                 id="velocidadDiariaPromedio"
//                 className="mb-0 d-flex"
//               >
//                 <Form.Check.Input
//                   type="checkbox"
//                   className="form-check-input-primary"
//                   onClick={() =>
//                     handleLegendToggle("Velocidadpromedio")
//                   }
//                   defaultChecked
//                 />
//                 <Form.Check.Label className="ps-2 fs-11 text-600 mb-0">
//                   Velocidad diario promedio:
//                   <span className="text-1100">{` ${velDiariaProm}`} km/h</span>
//                 </Form.Check.Label>
//               </Form.Check>
//               <Form.Check
//                 type="checkbox"
//                 id="ecommercePrevYear"
//                 className="mb-0 d-flex ps-0"
//               >
//                 <Form.Check.Input
//                   type="checkbox"
//                   className="ms-0 form-check-input-warning opacity-75"
//                   onClick={() => handleLegendToggle("Velocidadmaxima")}
//                   defaultChecked
//                 />
//                 <Form.Check.Label className="ps-2 fs-11 text-600 mb-0">
//                   Límite de velocidad:
//                   <span className="text-1100">{` ${velMaxima}`} km/h</span>
//                 </Form.Check.Label>
//               </Form.Check>
//             </Flex>
//           </Col>
//           {/**
//            * <Col xs="auto">
//             <CardDropdown />
//           </Col>
//            */}
//         </Row>
//       </Card.Header>
//       <Card.Body className="pe-xxl-0">
//         <VelocidadChart
//           data={isSingleDay ? groupedByHour : groupedByDate}
//           setVelDiariaProm={setVelDiariaProm}
//           setVelMaxima={setVelMaxima}
//           ref={chartRef}
//           isSingleDay={isSingleDay} // 🔹 Pasar esta información al gráfico
//           style={{ height: "8.625rem" }}
//         />
//       </Card.Body>
//     </Card>
//   );
// };

// export default Velocidad;
